// src/WarrantyTab.js
import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

async function apiFetch(path, { method = "GET", body, headers } = {}) {
  const base = process.env.REACT_APP_API_BASE;
  if (!base) throw new Error("Missing REACT_APP_API_BASE in .env");

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error("Missing ID token (please sign out/in again)");

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg.includes("HTTP") ? msg : `${msg} (HTTP ${res.status})`);
  }
  return data;
}

function ensureArrayLen(arr, n, makeItem) {
  const out = Array.isArray(arr) ? [...arr] : [];
  while (out.length < n) out.push(makeItem());
  if (out.length > n) out.length = n;
  return out;
}

// ✅ Others: rule normalizer
function buildOthersTextFromSuffix(suffixRaw) {
  var suffix = String(suffixRaw || "").trim();
  if (!suffix) return "Others:";
  return "Others: " + suffix;
}

function extractOthersSuffix(fullRaw) {
  var s = String(fullRaw || "").trim();
  if (!s) return "";
  if (s === "Others:") return "";
  if (s.startsWith("Others:")) {
    var rest = s.slice("Others:".length);
    rest = rest.replace(/^\s+/, "");
    return rest;
  }
  return s;
}

// ---------- Date helpers ----------
// UI date input uses YYYY-MM-DD. Forms usually want DD/MM/YYYY.
function isoToDmy(iso) {
  var s = String(iso || "").trim();
  if (!s) return "";
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

// If existing saved value is already DD/MM/YYYY, show it in date input (YYYY-MM-DD)
function dmyToIso(dmy) {
  var s = String(dmy || "").trim();
  if (!s) return "";
  var m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

// --- Admin detection (from Cognito ID token) ---
function parseJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return {};
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

// --- Light tinted section styles for readability ---
function getSectionTheme(kind) {
  if (kind === "purple") {
    return {
      wrap: { border: "1px solid #e9d5ff", background: "#faf5ff" },
      head: { background: "#f3e8ff", borderBottom: "1px solid #e9d5ff", color: "#6b21a8" },
      sub: { color: "#6b21a8" },
    };
  }
  if (kind === "orange") {
    return {
      wrap: { border: "1px solid #fed7aa", background: "#fff7ed" },
      head: { background: "#ffedd5", borderBottom: "1px solid #fed7aa", color: "#9a3412" },
      sub: { color: "#9a3412" },
    };
  }
  return {
    wrap: { border: "1px solid #e2e8f0", background: "white" },
    head: { background: "white", borderBottom: "1px solid #e2e8f0", color: "#0f172a" },
    sub: { color: "#334155" },
  };
}

// ✅ Orange Reason Code options (store ONLY the number)
const ORANGE_REASON_CODES = [
  { value: "1", label: "1: Customer ordered wrongly (Design change, etc.)" },
  { value: "2", label: "2: Unit faulty" },
  { value: "3", label: "3: Unit damaged in transit" },
  { value: "4", label: "4: Sales admin entry error" },
  { value: "5", label: "5: Wrong info given by sales" },
  { value: "6", label: "6: Wrong physical picking" },
  { value: "7", label: "7: Customer gave wrong delivery info" },
  { value: "8", label: "8: Delivered at wrong time by transport co." },
  { value: "9", label: "9: Customer cannot pay" },
  { value: "10", label: "10: Change account" },
  { value: "11", label: "11: On loan" },
  { value: "12", label: "12: Exhibition" },
  { value: "13", label: "13: Showroom" },
  { value: "14", label: "14: Others" },
];

/** ---------- Workflow helpers ---------- **/

function normUpper(v) {
  return String(v || "").trim().toUpperCase();
}

function deriveWorkflowStatus(wr) {
  // Primary source
  const ws = normUpper(wr?.workflowStatus);
  if (ws) return ws;

  // Fallback mapping for older records (best-effort)
  const submitStatus = normUpper(wr?.submitStatus || "DRAFT");
  const approvalStatus = normUpper(wr?.approvalStatus || "");
  const finalStatus = normUpper(wr?.finalStatus || "");

  if (submitStatus !== "SUBMITTED") return "DRAFT";

  if (approvalStatus === "REJECTED") return "REJECTED_BY_APPROVER";
  if (approvalStatus !== "APPROVED") return "PENDING_APPROVER";

  // approved but before Sales Admin 1
  const hasSalesAdmin1 = !!wr?.salesAdminSubmittedAt || normUpper(wr?.salesAdminStatus) === "SUBMITTED";
  if (!hasSalesAdmin1) return "PENDING_SALES_ADMIN_1";

  const hasLogistics = !!wr?.logisticsSubmittedAt || normUpper(wr?.logisticsStatus) === "SUBMITTED";
  if (!hasLogistics) return "PENDING_LOGISTICS";

  const hasFinal = !!wr?.finalSubmittedAt || normUpper(wr?.finalStatus) === "DONE" || finalStatus === "DONE";
  if (!hasFinal) return "PENDING_SALES_ADMIN_2";

  // If final submitted but purple pdf still pending, show completing
  const fps = wr?.finalPdfStatus || {};
  if (normUpper(fps?.purple) === "PENDING") return "COMPLETING";

  return "COMPLETED";
}

function workflowLabel(ws) {
  const m = {
    DRAFT: "Draft",
    PENDING_APPROVER: "Pending Approver",
    REJECTED_BY_APPROVER: "Rejected",
    PENDING_SALES_ADMIN_1: "Pending Sales Admin (1)",
    PENDING_LOGISTICS: "Pending Logistics",
    PENDING_SALES_ADMIN_2: "Pending Sales Admin (Final)",
    COMPLETING: "Generating final PDFs",
    COMPLETED: "Completed",
  };
  return m[ws] || ws || "-";
}

function stepIndexFromWorkflow(ws) {
  // Steps array is: [Agent, Approver, Sales Admin, Logistics, Sales Admin (Final)]
  // Return current active step number (1..5), and 6 for COMPLETED.
  if (ws === "DRAFT") return 1;

  if (ws === "PENDING_APPROVER" || ws === "REJECTED_BY_APPROVER") return 2;

  if (ws === "PENDING_SALES_ADMIN_1") return 3;

  if (ws === "PENDING_LOGISTICS") return 4;

  if (ws === "PENDING_SALES_ADMIN_2" || ws === "COMPLETING") return 5;

  if (ws === "COMPLETED") return 6;

  // default: be safe and show Approver step once submitted
  return 2;
}

function Pill({ text, tone }) {
  const map = {
    neutral: { bg: "#f8fafc", bd: "#e2e8f0", fg: "#334155" },
    green: { bg: "#dcfce7", bd: "#bbf7d0", fg: "#166534" },
    amber: { bg: "#fffbeb", bd: "#fde68a", fg: "#92400e" },
    red: { bg: "#fee2e2", bd: "#fecaca", fg: "#991b1b" },
    purple: { bg: "#f3e8ff", bd: "#e9d5ff", fg: "#6b21a8" },
  };
  const c = map[tone] || map.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        border: `1px solid ${c.bd}`,
        background: c.bg,
        color: c.fg,
        fontSize: 12,
        fontWeight: 900,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}

function StepTracker({ ws }) {
  const steps = ["Agent", "Approver", "Sales Admin", "Logistics", "Sales Admin (Final)"];
  const idx = stepIndexFromWorkflow(ws); // 1..5 (5 = completed)
  const isRejected = ws === "REJECTED_BY_APPROVER";

  const tone = isRejected ? "red" : ws === "COMPLETED" ? "green" : ws === "COMPLETING" ? "purple" : "amber";

  return (
    <div style={{ marginTop: 12, border: "1px solid #e2e8f0", borderRadius: 12, padding: 12, background: "white" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div style={{ fontWeight: 900 }}>Workflow</div>
        <Pill text={workflowLabel(ws)} tone={tone} />
      </div>

      <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {steps.map((name, i) => {
          const n = i + 1;
          const done = idx > n; // when idx=6 (COMPLETED), all n=1..5 are done
          const active = idx === n; // active only on that step
          const bg = done ? "#dcfce7" : active ? "#fffbeb" : "#f8fafc";
          const bd = done ? "#bbf7d0" : active ? "#fde68a" : "#e2e8f0";
          const fg = done ? "#166534" : active ? "#92400e" : "#64748b";
          const icon = done ? "✓" : active ? "●" : "○";

          return (
            <div
              key={name}
              style={{
                flex: "1 1 160px",
                minWidth: 160,
                border: `1px solid ${bd}`,
                background: bg,
                borderRadius: 12,
                padding: "10px 12px",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 900, color: fg }}>
                {icon} {name}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>{done ? "Done" : active ? "In progress" : "Pending"}</div>
            </div>
          );
        })}
      </div>

      {ws === "COMPLETING" ? (
        <div style={{ marginTop: 10, fontSize: 12, color: "#6b21a8", fontWeight: 800 }}>
          Final PDFs are being generated. Use “Refresh status” to check when it becomes COMPLETED.
        </div>
      ) : null}
    </div>
  );
}

/** ---------- Component ---------- **/

export default function WarrantyTab({ incidentDetails, profile, loadProfile }) {
  const incidentId = incidentDetails?.incidentId || incidentDetails?.id;

  const archived = String(incidentDetails?.status || "").toUpperCase() === "ARCHIVED";
  const hasArchivePdf = !!incidentDetails?.archivePdfKey;
  const ready = archived && hasArchivePdf;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wr, setWr] = useState(null);

  const [filesBusy, setFilesBusy] = useState(false);
  const [files, setFiles] = useState(null);

  const [isAdmin, setIsAdmin] = useState(false);

  const submitStatus = normUpper(wr?.submitStatus || "DRAFT");
  const submitted = submitStatus === "SUBMITTED";
  const disableEdits = busy || submitted;

  const ws = useMemo(() => deriveWorkflowStatus(wr), [wr]);

  const purpleItems = useMemo(() => {
    return ensureArrayLen(wr?.purpleAgentData?.items, 10, () => ({ desc: "", qty: "", instr: "" }));
  }, [wr?.purpleAgentData?.items]);

  const orangeModels = useMemo(() => {
    return ensureArrayLen(wr?.orangeAgentData?.models, 10, () => ({ model: "", qty: "", serial: "", credit: "" }));
  }, [wr?.orangeAgentData?.models]);

  const purpleTheme = useMemo(() => getSectionTheme("purple"), []);
  const orangeTheme = useMemo(() => getSectionTheme("orange"), []);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const payload = parseJwtPayload(idToken);
        const groups = payload["cognito:groups"] || [];
        const list = Array.isArray(groups) ? groups : String(groups).split(",");
        setIsAdmin(list.map((x) => String(x).trim()).includes("Admin"));
      } catch {
        setIsAdmin(false);
      }
    })();
  }, []);

  function withDefaultSig(obj) {
    const hasValue = typeof obj?.sig?.useMySignatureRequester === "boolean";
    if (hasValue) return obj; // user already chose true/false before → keep it

    return {
      ...obj,
      sig: { ...(obj.sig || {}), useMySignatureRequester: true }, // default only once
    };
  }

  async function loadOrCreate() {
    if (!incidentId) return null;
    setBusy(true);
    setError("");

    try {
      const resp = await apiFetch(`/warranty?incidentId=${encodeURIComponent(incidentId)}`);
      const latest = resp?.items?.[0] || null;

      if (latest) {
        const normalized = withDefaultSig(latest);
        setWr(normalized);
        return normalized;
      }

      const created = await apiFetch(`/warranty/create`, {
        method: "POST",
        body: {
          incidentId,
          defaults: {
            approverName: "William Chng",
            approverEmail: "william.chng@daikin.com.sg",
            salesAdminName: "Lee Di Wen",
            salesAdminEmail: "diwen.lee@daikin.com.sg",
            logisticsName: "Lim Shi Yan",
            logisticsEmail: "shiyan.lim@daikin.com.sg",
            // optional: you can also put sig default here if your backend stores it
            sig: { useMySignatureRequester: true },
          },
        },
      });

      const normalizedCreated = withDefaultSig(created);
      setWr(normalizedCreated);
      return normalizedCreated;
    } catch (e) {
      setError(e.message || String(e));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function loadFilesNow(wrId) {
    if (!wrId) return;
    setFilesBusy(true);
    try {
      const f = await apiFetch(`/warranty/${encodeURIComponent(wrId)}/files`);
      setFiles(f);
    } catch (e) {
      setError((prev) => prev || (e.message || String(e)));
    } finally {
      setFilesBusy(false);
    }
  }

  async function refreshAll() {
    if (!incidentId) return;
    setBusy(true);
    setError("");
    try {
      const latest = await loadOrCreate();
      if (latest?.wrId) await loadFilesNow(latest.wrId);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadOrCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  useEffect(() => {
    if (wr?.wrId) loadFilesNow(wr.wrId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wr?.wrId]);

  async function requestApprovalEmail({ silent } = { silent: false }) {
    if (!wr?.wrId) return;
    try {
      if (!silent) {
        setBusy(true);
        setError("");
      }
      await apiFetch(`/warranty/${encodeURIComponent(wr.wrId)}/request-approval`, {
        method: "POST",
        body: { incidentId },
      });
    } catch (e) {
      setError((prev) => prev || (e.message || String(e)));
    } finally {
      if (!silent) setBusy(false);
    }
  }

  async function saveDraftInternal({ silent } = { silent: false }) {
    if (!wr?.wrId) return null;
    if (submitted) return wr;

    if (!silent) {
      setBusy(true);
      setError("");
    }

    try {
      if (wr?.sig?.useMySignatureRequester) {
        if (!profile) await loadProfile({ silent: true });
      }

      const normalizedPurple = {
        ...(wr.purpleAgentData || {}),
        items: ensureArrayLen(wr?.purpleAgentData?.items, 10, () => ({ desc: "", qty: "", instr: "" })),
      };

      const normalizedOrange = {
        ...(wr.orangeAgentData || {}),
        // ✅ default reason code is "2" if empty
        reasonCode: String(wr?.orangeAgentData?.reasonCode || "").trim() || "2",
        models: ensureArrayLen(wr?.orangeAgentData?.models, 10, () => ({ model: "", qty: "", serial: "", credit: "" })),
        othersText: String(wr?.orangeAgentData?.othersText || "").startsWith("Others:")
          ? wr?.orangeAgentData?.othersText || "Others:"
          : buildOthersTextFromSuffix(wr?.orangeAgentData?.othersText),
      };

      const saved = await apiFetch(`/warranty/${encodeURIComponent(wr.wrId)}/agent`, {
        method: "PUT",
        body: {
          incidentId,
          coverNote: wr.coverNote || "",
          contacts: wr.contacts || {},
          formsEnabled: wr.formsEnabled || { purple: false, orange: false },
          purpleAgentData: normalizedPurple,
          orangeAgentData: normalizedOrange,
          sig: wr.sig || { useMySignatureRequester: true },
        },
      });

      setWr(saved);
      await loadFilesNow(saved?.wrId || wr.wrId);
      return saved;
    } finally {
      if (!silent) setBusy(false);
    }
  }

  async function saveDraft() {
    if (submitted) {
      setError("This warranty is already SUBMITTED. Editing/saving is locked.");
      return;
    }
    try {
      await saveDraftInternal({ silent: false });
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  async function submitWarranty() {
    if (!wr?.wrId) return;

    const shouldForce = submitStatus === "SUBMITTED";

    if (shouldForce) {
      const ok = window.confirm(
        "This warranty is already submitted.\n\nSubmit again will regenerate and overwrite existing Orange/Purple XLSX and PDF files in S3.\n\nContinue?"
      );
      if (!ok) return;
    }

    setBusy(true);
    setError("");

    try {
      if (!shouldForce) {
        await saveDraftInternal({ silent: true });
      }

      const path = shouldForce
        ? `/warranty/${encodeURIComponent(wr.wrId)}/submit?force=1`
        : `/warranty/${encodeURIComponent(wr.wrId)}/submit`;

      await apiFetch(path, { method: "POST", body: {} });

      await loadOrCreate();
      await loadFilesNow(wr.wrId);

      await requestApprovalEmail({ silent: true });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function reopenToDraft() {
    if (!wr?.wrId) return;

    if (!isAdmin) {
      setError("Admin only.");
      return;
    }

    if (!submitted) {
      setError("This warranty is already in DRAFT.");
      return;
    }

    const ok = window.confirm(
      "Unlock will set this Warranty back to DRAFT so you can edit it again.\n\nThis action is logged for audit.\n\nContinue?"
    );
    if (!ok) return;

    setBusy(true);
    setError("");

    try {
      await apiFetch(`/warranty/${encodeURIComponent(wr.wrId)}/reopen`, {
        method: "POST",
        body: { incidentId },
      });

      await loadOrCreate();
      await loadFilesNow(wr.wrId);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  function updatePurpleField(key, value) {
    setWr({
      ...wr,
      purpleAgentData: { ...(wr.purpleAgentData || {}), [key]: value },
    });
  }

  function updatePurpleItem(idx, patch) {
    const next = ensureArrayLen(wr?.purpleAgentData?.items, 10, () => ({ desc: "", qty: "", instr: "" }));
    next[idx] = { ...(next[idx] || {}), ...(patch || {}) };
    setWr({
      ...wr,
      purpleAgentData: { ...(wr.purpleAgentData || {}), items: next },
    });
  }

  function updateOrangeField(key, value) {
    setWr({
      ...wr,
      orangeAgentData: { ...(wr.orangeAgentData || {}), [key]: value },
    });
  }

  function updateOrangeModel(idx, patch) {
    const next = ensureArrayLen(wr?.orangeAgentData?.models, 10, () => ({ model: "", qty: "", serial: "", credit: "" }));
    next[idx] = { ...(next[idx] || {}), ...(patch || {}) };
    setWr({
      ...wr,
      orangeAgentData: { ...(wr.orangeAgentData || {}), models: next },
    });
  }

  function openUrl(url) {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (!ready) {
    return (
      <div style={{ padding: 24, color: "#64748b" }}>
        Warranty is available only when the incident is <b>ARCHIVED</b> and the Archive PDF has been generated.
      </div>
    );
  }

  const orangePdfUrl = files?.urls?.orangePdfUrl;
  const orangeXlsxUrl = files?.urls?.orangeXlsxUrl;
  const purplePdfUrl = files?.urls?.purplePdfUrl;
  const purpleXlsxUrl = files?.urls?.purpleXlsxUrl;

  const anyFormEnabled = !!wr?.formsEnabled?.orange || !!wr?.formsEnabled?.purple;

  return (
    <div style={{ padding: 24 }}>
      {error ? <div style={{ color: "#b91c1c", marginBottom: 10 }}>{error}</div> : null}
      {busy && !wr ? <div>Loading...</div> : null}

      {wr ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 900 }}>Warranty</div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              <div
                style={{
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: "1px solid #e2e8f0",
                  fontSize: 12,
                  fontWeight: 900,
                  color: submitStatus === "SUBMITTED" ? "#166534" : "#334155",
                  background: submitStatus === "SUBMITTED" ? "#dcfce7" : "#f8fafc",
                }}
                title="Warranty submit status"
              >
                {submitStatus}
              </div>

              <button
                onClick={refreshAll}
                disabled={busy || filesBusy}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #e2e8f0",
                  background: "white",
                  fontWeight: 900,
                }}
                title="Refresh warranty + file status"
              >
                {busy || filesBusy ? "Refreshing..." : "Refresh status"}
              </button>

              <button
                onClick={() => loadFilesNow(wr.wrId)}
                disabled={filesBusy || busy}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #e2e8f0",
                  background: "white",
                  fontWeight: 900,
                }}
              >
                {filesBusy ? "Refreshing..." : "Refresh files"}
              </button>
            </div>
          </div>

          {/* ✅ Workflow tracker */}
          <StepTracker ws={ws} />

          {submitted ? (
            <div
              style={{
                marginTop: 10,
                padding: 10,
                borderRadius: 12,
                background: "#fff7ed",
                border: "1px solid #fed7aa",
              }}
            >
              <div style={{ fontWeight: 900, color: "#9a3412" }}>Editing is locked</div>
              <div style={{ fontSize: 12, color: "#9a3412", marginTop: 4 }}>
                Admin can <b>Unlock</b> to edit again. Otherwise, clicking <b>Submit</b> again will <b>force regenerate</b>{" "}
                and overwrite PDFs/XLSX using the last saved draft data.
              </div>
            </div>
          ) : null}

          {/* Downloads */}
          <div style={{ marginTop: 12, border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 8 }}>Generated files</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: 12 }}>
                <div style={{ fontWeight: 900, marginBottom: 10 }}>Orange</div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => openUrl(orangePdfUrl)}
                    disabled={!orangePdfUrl}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      fontWeight: 900,
                      opacity: orangePdfUrl ? 1 : 0.5,
                      background: "white",
                    }}
                  >
                    Open PDF
                  </button>

                  <button
                    onClick={() => openUrl(orangeXlsxUrl)}
                    disabled={!orangeXlsxUrl}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      fontWeight: 900,
                      opacity: orangeXlsxUrl ? 1 : 0.5,
                      background: "white",
                    }}
                  >
                    Download XLSX
                  </button>
                </div>

                <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                  {orangePdfUrl || orangeXlsxUrl ? "Ready." : "Not generated yet (submit first)."}
                </div>
              </div>

              <div style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: 12 }}>
                <div style={{ fontWeight: 900, marginBottom: 10 }}>Purple</div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => openUrl(purplePdfUrl)}
                    disabled={!purplePdfUrl}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      fontWeight: 900,
                      opacity: purplePdfUrl ? 1 : 0.5,
                      background: "white",
                    }}
                  >
                    Open PDF
                  </button>

                  <button
                    onClick={() => openUrl(purpleXlsxUrl)}
                    disabled={!purpleXlsxUrl}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      fontWeight: 900,
                      opacity: purpleXlsxUrl ? 1 : 0.5,
                      background: "white",
                    }}
                  >
                    Download XLSX
                  </button>
                </div>

                <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                  {purplePdfUrl || purpleXlsxUrl ? "Ready." : "Not generated yet (submit first)."}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
              wrId: <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{wr.wrId}</span>
            </div>
          </div>

          <label style={{ display: "block", fontSize: 13, fontWeight: 800, color: "#334155", marginTop: 14 }}>
            Cover note
          </label>
          <textarea
            value={wr.coverNote || ""}
            disabled={disableEdits}
            onChange={(e) => setWr({ ...wr, coverNote: e.target.value })}
            style={{
              width: "100%",
              minHeight: 120,
              marginTop: 6,
              padding: 10,
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              background: disableEdits ? "#f8fafc" : "white",
            }}
          />

          {/* Contacts */}
          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {[
              ["Approver", "approverName", "approverEmail"],
              ["Sales Admin", "salesAdminName", "salesAdminEmail"],
              ["Logistics", "logisticsName", "logisticsEmail"],
            ].map(([title, nameKey, emailKey]) => (
              <div key={title} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>{title}</div>

                <div style={{ fontSize: 12, fontWeight: 800 }}>Name</div>
                <input
                  value={wr.contacts?.[nameKey] || ""}
                  disabled={disableEdits}
                  onChange={(e) => setWr({ ...wr, contacts: { ...(wr.contacts || {}), [nameKey]: e.target.value } })}
                  style={{
                    width: "100%",
                    padding: 8,
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    background: disableEdits ? "#f8fafc" : "white",
                  }}
                />

                <div style={{ fontSize: 12, fontWeight: 800, marginTop: 10 }}>Email</div>
                <input
                  value={wr.contacts?.[emailKey] || ""}
                  disabled={disableEdits}
                  onChange={(e) => setWr({ ...wr, contacts: { ...(wr.contacts || {}), [emailKey]: e.target.value } })}
                  style={{
                    width: "100%",
                    padding: 8,
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    background: disableEdits ? "#f8fafc" : "white",
                  }}
                />
              </div>
            ))}
          </div>

          {/* Form enables */}
          <div style={{ marginTop: 14, display: "flex", gap: 18, flexWrap: "wrap" }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
              <input
                type="checkbox"
                checked={!!wr.formsEnabled?.purple}
                disabled={disableEdits}
                onChange={(e) => setWr({ ...wr, formsEnabled: { ...(wr.formsEnabled || {}), purple: e.target.checked } })}
              />
              Create Purple form
            </label>

            <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
              <input
                type="checkbox"
                checked={!!wr.formsEnabled?.orange}
                disabled={disableEdits}
                onChange={(e) => setWr({ ...wr, formsEnabled: { ...(wr.formsEnabled || {}), orange: e.target.checked } })}
              />
              Create Orange form
            </label>
          </div>

          {/* Purple Form Fields */}
          {wr.formsEnabled?.purple ? (
            <div style={{ marginTop: 16, borderRadius: 14, overflow: "hidden", ...purpleTheme.wrap }}>
              <div style={{ padding: "10px 12px", fontWeight: 900, ...purpleTheme.head }}>Request for Delivery Form</div>

              <div style={{ padding: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Customer</div>
                    <input
                      value={wr.purpleAgentData?.customer || ""}
                      disabled={disableEdits}
                      onChange={(e) => updatePurpleField("customer", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />
                  </div>

                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Date of Request</div>
                    <input
                      type="date"
                      value={dmyToIso(wr.purpleAgentData?.dateOfRequest) || (wr.purpleAgentData?.dateOfRequest || "")}
                      disabled={disableEdits}
                      onChange={(e) => updatePurpleField("dateOfRequest", isoToDmy(e.target.value))}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Project Name</div>
                    <input
                      value={wr.purpleAgentData?.projectName || ""}
                      disabled={disableEdits}
                      onChange={(e) => updatePurpleField("projectName", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />
                  </div>

                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Place of Delivery (Line 1)</div>
                    <input
                      value={wr.purpleAgentData?.deliveryPlace1 || ""}
                      disabled={disableEdits}
                      onChange={(e) => updatePurpleField("deliveryPlace1", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />
                  </div>

                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Place of Delivery (Line 2)</div>
                    <input
                      value={wr.purpleAgentData?.deliveryPlace2 || ""}
                      disabled={disableEdits}
                      onChange={(e) => updatePurpleField("deliveryPlace2", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Requester Name</div>
                    <input
                      value={wr.purpleAgentData?.requesterName || ""}
                      disabled={disableEdits}
                      onChange={(e) => updatePurpleField("requesterName", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 12, fontSize: 12, fontWeight: 900, color: purpleTheme.sub.color }}>Equipment / Items</div>

                {purpleItems.map((it, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginTop: 10,
                      padding: 10,
                      border: "1px solid #f1f5f9",
                      borderRadius: 12,
                      background: "#ffffff",
                      opacity: disableEdits ? 0.9 : 1,
                    }}
                  >
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>Item {idx + 1}</div>

                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Description</div>
                        <input
                          value={it?.desc || ""}
                          disabled={disableEdits}
                          onChange={(e) => updatePurpleItem(idx, { desc: e.target.value })}
                          style={{
                            width: "100%",
                            padding: 8,
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            background: disableEdits ? "#f8fafc" : "white",
                          }}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Qty</div>
                        <input
                          value={it?.qty || ""}
                          disabled={disableEdits}
                          onChange={(e) => updatePurpleItem(idx, { qty: e.target.value })}
                          style={{
                            width: "100%",
                            padding: 8,
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            background: disableEdits ? "#f8fafc" : "white",
                          }}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Instructions</div>
                        <input
                          value={it?.instr || ""}
                          disabled={disableEdits}
                          onChange={(e) => updatePurpleItem(idx, { instr: e.target.value })}
                          style={{
                            width: "100%",
                            padding: 8,
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            background: disableEdits ? "#f8fafc" : "white",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Orange Form Fields */}
          {wr.formsEnabled?.orange ? (
            <div style={{ marginTop: 16, borderRadius: 14, overflow: "hidden", ...orangeTheme.wrap }}>
              <div style={{ padding: "10px 12px", fontWeight: 900, ...orangeTheme.head }}>Form for Returned Goods</div>

              <div style={{ padding: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    ["Sales in charge", "salesInCharge"],
                    ["D/O Number", "doNumber"],
                    ["Customer name", "customerName"],
                    ["Delivery date", "deliveryDate"],
                    ["Invoice number", "invoiceNumber"],
                    ["Project/Site", "projectSite"],
                    ["Reason code", "reasonCode"],
                    ["Expected return date", "expectedReturnDate"],
                    ["Detail reason", "detailReason1"],
                    ["Detail reason (line 2)", "detailReason2"],
                  ].map(([label, key]) => {
                    const isDate = key === "deliveryDate" || key === "expectedReturnDate";
                    const isReasonCode = key === "reasonCode";

                    const reasonValue = String(wr.orangeAgentData?.reasonCode || "").trim() || "2";

                    return (
                      <div
                        key={key}
                        style={{
                          gridColumn: key === "detailReason1" || key === "detailReason2" ? "1 / -1" : undefined,
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 800 }}>{label}</div>

                        {isReasonCode ? (
                          <select
                            value={reasonValue}
                            disabled={disableEdits}
                            onChange={(e) => updateOrangeField("reasonCode", e.target.value)} // ✅ stores ONLY number
                            style={{
                              width: "100%",
                              padding: 8,
                              border: "1px solid #e2e8f0",
                              borderRadius: 10,
                              background: disableEdits ? "#f8fafc" : "white",
                            }}
                          >
                            {ORANGE_REASON_CODES.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={isDate ? "date" : "text"}
                            value={
                              isDate
                                ? dmyToIso(wr.orangeAgentData?.[key]) || (wr.orangeAgentData?.[key] || "")
                                : wr.orangeAgentData?.[key] || ""
                            }
                            disabled={disableEdits}
                            onChange={(e) => updateOrangeField(key, isDate ? isoToDmy(e.target.value) : e.target.value)}
                            style={{
                              width: "100%",
                              padding: 8,
                              border: "1px solid #e2e8f0",
                              borderRadius: 10,
                              background: disableEdits ? "#f8fafc" : "white",
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 18, flexWrap: "wrap" }}>
                  {[
                    ["Free Replacement", "freeReplacement"],
                    ["Physical Return", "physicalReturn"],
                    ["Put back to Sales Order", "putBackToSalesOrder"],
                  ].map(([label, key]) => (
                    <label key={key} style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.[key]}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField(key, e.target.checked)}
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <div style={{ marginTop: 12, fontSize: 12, fontWeight: 900, color: orangeTheme.sub.color }}>Models</div>

                {orangeModels.map((m, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginTop: 10,
                      padding: 10,
                      border: "1px solid #f1f5f9",
                      borderRadius: 12,
                      background: "white",
                    }}
                  >
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>Model {idx + 1}</div>

                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Model</div>
                        <input
                          value={m?.model || ""}
                          disabled={disableEdits}
                          onChange={(e) => updateOrangeModel(idx, { model: e.target.value })}
                          style={{
                            width: "100%",
                            padding: 8,
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            background: disableEdits ? "#f8fafc" : "white",
                          }}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Qty</div>
                        <input
                          value={m?.qty || ""}
                          disabled={disableEdits}
                          onChange={(e) => updateOrangeModel(idx, { qty: e.target.value })}
                          style={{
                            width: "100%",
                            padding: 8,
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            background: disableEdits ? "#f8fafc" : "white",
                          }}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Serial</div>
                        <input
                          value={m?.serial || ""}
                          disabled={disableEdits}
                          onChange={(e) => updateOrangeModel(idx, { serial: e.target.value })}
                          style={{
                            width: "100%",
                            padding: 8,
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            background: disableEdits ? "#f8fafc" : "white",
                          }}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Credit</div>
                        <input
                          value={m?.credit || ""}
                          disabled={disableEdits}
                          onChange={(e) => updateOrangeModel(idx, { credit: e.target.value })}
                          style={{
                            width: "100%",
                            padding: 8,
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            background: disableEdits ? "#f8fafc" : "white",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Charges section (kept as-is from your code) */}
                <div
                  style={{
                    marginTop: 14,
                    padding: 12,
                    border: "1px solid #fed7aa",
                    borderRadius: 12,
                    background: "white",
                  }}
                >
                  <div style={{ fontWeight: 900, marginBottom: 10, color: "#9a3412" }}>Charges / Packaging</div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr",
                      gap: 10,
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.newPackingOpenedChecked}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("newPackingOpenedChecked", e.target.checked)}
                      />
                      New packing but opened
                    </label>

                    <input
                      type="number"
                      placeholder="QTY"
                      value={wr.orangeAgentData?.newPackingOpenedQty || ""}
                      disabled={disableEdits}
                      onChange={(e) => updateOrangeField("newPackingOpenedQty", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />

                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.newPackingOpenedWaive}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("newPackingOpenedWaive", e.target.checked)}
                      />
                      Waive charges
                    </label>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr",
                      gap: 10,
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.noPackingDeformedWrongChecked}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("noPackingDeformedWrongChecked", e.target.checked)}
                      />
                      No packing / packing deformed / wrong packing
                    </label>

                    <input
                      type="number"
                      placeholder="QTY"
                      value={wr.orangeAgentData?.noPackingDeformedWrongQty || ""}
                      disabled={disableEdits}
                      onChange={(e) => updateOrangeField("noPackingDeformedWrongQty", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />

                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.noPackingDeformedWrongWaive}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("noPackingDeformedWrongWaive", e.target.checked)}
                      />
                      Waive charges
                    </label>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 2fr 1fr",
                      gap: 10,
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.missingAccessoriesChecked}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("missingAccessoriesChecked", e.target.checked)}
                      />
                      Missing accessories
                    </label>

                    <input
                      type="text"
                      placeholder="Accessories (describe)"
                      value={wr.orangeAgentData?.missingAccessoriesText || ""}
                      disabled={disableEdits}
                      onChange={(e) => updateOrangeField("missingAccessoriesText", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />

                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800, justifyContent: "flex-end" }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.missingAccessoriesWaive}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("missingAccessoriesWaive", e.target.checked)}
                      />
                      Waive charges
                    </label>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontWeight: 800 }}>Admin Charges</div>

                    <input
                      type="number"
                      placeholder="QTY"
                      value={wr.orangeAgentData?.adminChargesQty || ""}
                      disabled={disableEdits}
                      onChange={(e) => updateOrangeField("adminChargesQty", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />

                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.adminChargesWaive}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("adminChargesWaive", e.target.checked)}
                      />
                      Waive charges
                    </label>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontWeight: 800 }}>Transportation Fee (S$)</div>

                    <input
                      type="number"
                      placeholder="Fee"
                      value={wr.orangeAgentData?.transportationFee || ""}
                      disabled={disableEdits}
                      onChange={(e) => updateOrangeField("transportationFee", e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: disableEdits ? "#f8fafc" : "white",
                      }}
                    />

                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={!!wr.orangeAgentData?.transportationFeeWaive}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("transportationFeeWaive", e.target.checked)}
                      />
                      Waive charges
                    </label>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr", gap: 10, alignItems: "center" }}>
                    <div style={{ fontWeight: 800 }}>Others</div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <div
                        style={{
                          padding: "8px 10px",
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          background: "#f8fafc",
                          fontWeight: 800,
                          color: "#334155",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Others:
                      </div>

                      <input
                        type="text"
                        placeholder="Type details..."
                        value={extractOthersSuffix(wr.orangeAgentData?.othersText)}
                        disabled={disableEdits}
                        onChange={(e) => {
                          const suffix = e.target.value;
                          updateOrangeField("othersText", buildOthersTextFromSuffix(suffix));
                        }}
                        style={{
                          width: "100%",
                          padding: 8,
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          background: disableEdits ? "#f8fafc" : "white",
                        }}
                      />
                    </div>

                    <label style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
                      <input
                        type="number"
                        placeholder="Fee"
                        value={wr.orangeAgentData?.othersFee || ""}
                        disabled={disableEdits}
                        onChange={(e) => updateOrangeField("othersFee", e.target.value)}
                        style={{
                          width: 140,
                          padding: 8,
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          background: disableEdits ? "#f8fafc" : "white",
                        }}
                      />

                      <span style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
                        <input
                          type="checkbox"
                          checked={!!wr.orangeAgentData?.othersWaive}
                          disabled={disableEdits}
                          onChange={(e) => updateOrangeField("othersWaive", e.target.checked)}
                        />
                        Waive
                      </span>
                    </label>
                  </div>

                  <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                    Note: “Others” will only be saved if it begins with <b>Others:</b>. Otherwise it will be stored as <b>Others:</b>.
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>Total (S$)</div>
                  <input
                    value={wr.orangeAgentData?.totalSgd || ""}
                    disabled={disableEdits}
                    onChange={(e) => updateOrangeField("totalSgd", e.target.value)}
                    style={{
                      width: "100%",
                      padding: 8,
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      background: disableEdits ? "#f8fafc" : "white",
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {/* Requester signature checkbox */}
          <div style={{ marginTop: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800 }}>
              <input
                type="checkbox"
                checked={!!wr.sig?.useMySignatureRequester}
                disabled={disableEdits}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  setWr({ ...wr, sig: { ...(wr.sig || {}), useMySignatureRequester: checked } });
                  if (checked) {
                    if (!profile) await loadProfile({ silent: true });
                  }
                }}
              />
              Use my signature (Requester)
            </label>
          </div>

          {/* Actions */}
          <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {isAdmin && submitted ? (
              <button
                onClick={reopenToDraft}
                disabled={busy}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid #e2e8f0",
                  fontWeight: 900,
                  background: "#ffffff",
                  color: "#0f172a",
                  opacity: busy ? 0.7 : 1,
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Admin only: unlock warranty back to draft status"
              >
                {busy ? "Working..." : "Unlock (Reopen Draft)"}
              </button>
            ) : null}

            <button
              onClick={saveDraft}
              disabled={busy || submitted}
              style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #e2e8f0", fontWeight: 900 }}
              title={submitted ? "Locked after SUBMITTED" : "Save draft"}
            >
              {busy ? "Saving..." : submitted ? "Save locked" : "Save draft"}
            </button>

            <button
              onClick={submitWarranty}
              disabled={!anyFormEnabled || busy}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                fontWeight: 900,
                background: !anyFormEnabled || busy ? "#94a3b8" : "#0f172a",
                color: "white",
                opacity: !anyFormEnabled || busy ? 0.7 : 1,
                cursor: !anyFormEnabled || busy ? "not-allowed" : "pointer",
              }}
              title={
                !anyFormEnabled
                  ? "Enable Orange and/or Purple first"
                  : submitStatus === "SUBMITTED"
                  ? "Already submitted: clicking Submit will force regenerate (overwrite files)"
                  : "Generates filled XLSX + PDF (Orange/Purple) into S3, then emails approver"
              }
            >
              {busy ? "Submitting..." : submitStatus === "SUBMITTED" ? "Submit (regenerate)" : "Submit"}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}